import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../components/ThemeContext';
import { useGlobalFilters } from '../components/GlobalFilterContext';

// ============================================================
// TYPES
// ============================================================
export interface ManufacturerSummary {
  id: number;
  name: string;
  country?: string;
  address?: string;
  dea_number?: string;
  product_count: number;
  recall_count: number;
  active_recall_count: number;
  shortage_count: number;
  ccp_flag?: boolean;
}

type SortField = 'name' | 'country' | 'product_count' | 'recall_count' | 'shortage_count';
type SortDir = 'asc' | 'desc';
type ViewMode = 'table' | 'card';

interface Filters {
  country: string;
  has_recalls: string;
  has_shortages: string;
  coo_confidence: string;
  ccp_flag: string;
}

interface ManufacturerListPageProps {
  apiBase: string;
  onViewManufacturer: (id: number) => void;
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  IconSearch: React.ComponentType;
  IconSpinner: React.ComponentType;
  IconChevron: React.ComponentType;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  IconDatabase: React.ComponentType;
  IconShield: React.ComponentType;
  IconAlert: React.ComponentType;
  IconFlag: React.ComponentType;
}

// ============================================================
// HELPER: useMediaQuery hook
// ============================================================
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function ManufacturerListPage({
  apiBase,
  onViewManufacturer,
  SectionHeader,
  IconSearch,
  IconSpinner,
  IconChevron,
  StatCard,
  IconDatabase,
  IconShield,
  IconAlert,
  IconFlag,
}: ManufacturerListPageProps) {
  const { T } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { filters: globalFilters } = useGlobalFilters();

  // Helper: derive local Filters from global filters
  const deriveFiltersFromGlobal = useCallback((): Filters => {
    // Pass ALL selected countries as comma-separated string for multi-select
    const country = globalFilters.country.length > 0
      ? globalFilters.country.join(',')
      : '';
    // Pass ALL selected COO confidence tiers as comma-separated (backend supports multi-select)
    const coo_confidence = globalFilters.cooConfidence.length > 0
      ? globalFilters.cooConfidence.join(',')
      : '';
    const ccp_flag = globalFilters.ccpFlag || '';
    return { country, has_recalls: '', has_shortages: '', coo_confidence, ccp_flag };
  }, [globalFilters.country, globalFilters.cooConfidence, globalFilters.ccpFlag]);

  // State — initialized from global filters so page loads with correct data
  const [filters, setFilters] = useState<Filters>(deriveFiltersFromGlobal);

  // Sync local filters from global filters when they change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      country: globalFilters.country.length > 0 ? globalFilters.country.join(',') : '',
      coo_confidence: globalFilters.cooConfidence.length > 0 ? globalFilters.cooConfidence.join(',') : '',
      ccp_flag: globalFilters.ccpFlag || '',
    }));
  }, [globalFilters.country, globalFilters.cooConfidence, globalFilters.ccpFlag]);

  // Set search to globally-selected manufacturer name when applicable
  useEffect(() => {
    if (globalFilters.manufacturer.length > 0) {
      setSearchInput(globalFilters.manufacturer[0]);
    }
  }, [globalFilters.manufacturer]);
  const [sortField, setSortField] = useState<SortField>('product_count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? 'card' : 'table');
  const [viewOverridden, setViewOverridden] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Server data
  const [manufacturers, setManufacturers] = useState<ManufacturerSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [aggregates, setAggregates] = useState<{
    total_with_active_recalls: number;
    total_with_shortages: number;
    total_products_covered: number;
    total_not_linked: number;
  }>({ total_with_active_recalls: 0, total_with_shortages: 0, total_products_covered: 0, total_not_linked: 0 });

  const hasActiveFilters = filters.country || filters.has_recalls || filters.has_shortages || filters.coo_confidence || filters.ccp_flag;

  // Fetch manufacturers from backend with server-side pagination, sorting, filtering
  const fetchManufacturers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String((currentPage - 1) * pageSize));
      params.set('sort_field', sortField);
      params.set('sort_dir', sortDir);
      if (searchQuery) params.set('search', searchQuery);
      if (filters.country) params.set('country', filters.country);
      if (filters.has_recalls) params.set('has_recalls', filters.has_recalls);
      if (filters.has_shortages) params.set('has_shortages', filters.has_shortages);
      if (filters.coo_confidence) params.set('coo_confidence', filters.coo_confidence);
      if (filters.ccp_flag) params.set('ccp_flag', filters.ccp_flag);

      const res = await fetch(`${apiBase}/metasupply/manufacturers?${params.toString()}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setManufacturers(data.manufacturers || []);
        setTotal(data.total || 0);
        if (data.aggregates) {
          setAggregates(data.aggregates);
        }
      }
    } catch (err) {
      console.error('Failed to fetch manufacturers:', err);
      setManufacturers([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, currentPage, pageSize, sortField, sortDir, searchQuery, filters]);

  useEffect(() => { fetchManufacturers(); }, [fetchManufacturers]);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.country, filters.has_recalls, filters.has_shortages, filters.coo_confidence, filters.ccp_flag, sortField, sortDir, searchQuery]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Sync view mode with screen size
  useEffect(() => {
    if (!viewOverridden) {
      setViewMode(isMobile ? 'card' : 'table');
    }
  }, [isMobile, viewOverridden]);

  const handleViewToggle = (mode: ViewMode) => {
    setViewMode(mode);
    setViewOverridden(true);
  };

  // Sort toggle handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'name' || field === 'country' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span style={{ color: T.textLight, fontSize: 10, marginLeft: 4 }}>{"\u2195"}</span>;
    return <span style={{ color: T.navy, fontSize: 10, marginLeft: 4 }}>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>;
  };

  // CSV export
  const [exporting, setExporting] = useState(false);
  const handleExportCSV = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '10000');
      params.set('offset', '0');
      params.set('sort_field', sortField);
      params.set('sort_dir', sortDir);
      if (searchQuery) params.set('search', searchQuery);
      if (filters.country) params.set('country', filters.country);
      if (filters.has_recalls) params.set('has_recalls', filters.has_recalls);
      if (filters.has_shortages) params.set('has_shortages', filters.has_shortages);
      if (filters.coo_confidence) params.set('coo_confidence', filters.coo_confidence);
      if (filters.ccp_flag) params.set('ccp_flag', filters.ccp_flag);

      const res = await fetch(`${apiBase}/metasupply/manufacturers?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      const allMfrs: ManufacturerSummary[] = data.manufacturers;

      const csvHeaders = ['Name', 'Country', 'Products', 'CCP Flag', 'Total Recalls', 'Active Recalls', 'Active Shortages', 'DEA Number'];
      const escapeCSV = (val: string): string => {
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      const rows = allMfrs.map(m => [
        m.name || '',
        m.country || '',
        String(m.product_count),
        m.ccp_flag ? 'Yes' : 'No',
        String(m.recall_count),
        String(m.active_recall_count),
        String(m.shortage_count),
        m.dea_number || '',
      ].map(escapeCSV));

      const csvContent = [csvHeaders.map(escapeCSV).join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `manufacturers-export-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setExporting(false);
    }
  }, [apiBase, sortField, sortDir, searchQuery, filters]);

  const thStyle: React.CSSProperties = {
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    color: T.textLight,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    textAlign: "left",
    borderBottom: `2px solid ${T.border}`,
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  // Pagination controls
  const renderPagination = () => {
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, total);

    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 0",
        flexWrap: "wrap",
        gap: 12,
      }}>
        <span style={{ fontSize: 13, color: T.textLight }}>
          Showing <strong style={{ color: T.textDark }}>{startItem}</strong> &ndash; <strong style={{ color: T.textDark }}>{endItem}</strong> of <strong style={{ color: T.textDark }}>{total.toLocaleString()}</strong> manufacturers
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            style={{
              padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.border}`,
              background: currentPage <= 1 ? T.offWhite : T.white,
              color: currentPage <= 1 ? T.textLight : T.textDark,
              cursor: currentPage <= 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600,
            }}
          >
            Prev
          </button>
          {pages.map((p, idx) =>
            typeof p === 'string' ? (
              <span key={`ellipsis-${idx}`} style={{ padding: "6px 8px", fontSize: 13, color: T.textLight }}>...</span>
            ) : (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                style={{
                  padding: "6px 12px", borderRadius: 6, fontSize: 13, fontWeight: 600,
                  border: `1px solid ${p === currentPage ? T.navy : T.border}`,
                  background: p === currentPage ? T.navy : T.white,
                  color: p === currentPage ? T.white : T.textDark,
                  cursor: "pointer",
                  minWidth: 36,
                }}
              >
                {p}
              </button>
            )
          )}
          <button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            style={{
              padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.border}`,
              background: currentPage >= totalPages ? T.offWhite : T.white,
              color: currentPage >= totalPages ? T.textLight : T.textDark,
              cursor: currentPage >= totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600,
            }}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Card view for a manufacturer
  const renderManufacturerCard = (m: ManufacturerSummary) => {
    const hasActiveRecalls = m.active_recall_count > 0;
    const hasShortage = m.shortage_count > 0;
    const hasCcpFlag = !!m.ccp_flag;

    return (
      <div
        key={m.id}
        onClick={() => onViewManufacturer(m.id)}
        style={{
          background: T.white,
          border: `1px solid ${hasCcpFlag ? T.danger + '60' : hasActiveRecalls ? T.danger + '60' : T.border}`,
          borderRadius: T.radius,
          padding: 16,
          cursor: "pointer",
          boxShadow: T.shadow,
          transition: "box-shadow 0.2s",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {m.name}
            </div>
            {m.country && (
              <div style={{ fontSize: 12, color: T.textMid }}>
                {m.country}
              </div>
            )}
          </div>
          <div style={{ flexShrink: 0, marginLeft: 12, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>{m.product_count}</span>
            <span style={{ fontSize: 10, color: T.textLight, fontWeight: 600 }}>Products</span>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {hasCcpFlag && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.04em",
              background: T.dangerBg, color: T.danger, border: `1px solid ${T.danger}40`,
            }}>
              CCP Alert
            </span>
          )}
          {hasActiveRecalls && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.04em",
              background: T.dangerBg, color: T.danger, border: `1px solid ${T.danger}40`,
            }}>
              {m.active_recall_count} Active Recall{m.active_recall_count !== 1 ? 's' : ''}
            </span>
          )}
          {hasShortage && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.04em",
              background: T.warningBg, color: T.warning, border: `1px solid ${T.warning}40`,
            }}>
              {m.shortage_count} Shortage{m.shortage_count !== 1 ? 's' : ''}
            </span>
          )}
          {!hasCcpFlag && !hasActiveRecalls && !hasShortage && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.04em",
              background: T.successBg, color: T.success, border: "1px solid #BBF7D0",
            }}>
              Clean Record
            </span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textLight }}>
          <span>{m.recall_count} total recall{m.recall_count !== 1 ? 's' : ''}</span>
          <span style={{ color: T.textLight }}><IconChevron /></span>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Page Header + Search */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 16, flexDirection: isMobile ? "column" : "row" }}>
        <SectionHeader sub={`${total.toLocaleString()} manufacturers tracked`}>Manufacturers</SectionHeader>
        <div style={{ minWidth: isMobile ? "100%" : 320, maxWidth: isMobile ? "100%" : 420, width: "100%", position: "relative" }}>
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.textLight }}>
            <IconSearch />
          </span>
          <input
            type="text"
            placeholder="Search manufacturers..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{
              width: "100%", padding: "10px 14px 10px 40px",
              border: `1.5px solid ${T.border}`, borderRadius: T.radiusSm,
              fontSize: 14, color: T.textDark, background: T.white,
              outline: "none", transition: "border-color 0.2s",
            }}
            onFocus={e => { e.target.style.borderColor = T.navy; }}
            onBlur={e => { e.target.style.borderColor = T.border; }}
          />
        </div>
      </div>

      {/* Main View */}
      {loading && manufacturers.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IconSpinner /></div>
      ) : (
        <>
          {/* Summary Stat Cards */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
            gap: 16,
          }}>
            <StatCard
              title="Manufacturers"
              value={total.toLocaleString()}
              color={T.navy}
              accent="#000000"
              icon={<IconDatabase />}
            />
            <StatCard
              title="Drugs Linked"
              value={aggregates.total_products_covered.toLocaleString()}
              color={T.success}
              accent="#000000"
              icon={<IconShield />}
            />
            <StatCard
              title="Not Linked"
              value={aggregates.total_not_linked.toLocaleString()}
              color={T.textMid}
              accent="#000000"
              icon={<IconShield />}
            />
            <StatCard
              title="Recalls"
              value={aggregates.total_with_active_recalls.toLocaleString()}
              color={T.danger}
              accent="#000000"
              icon={<IconAlert />}
            />
            <StatCard
              title="Shortages"
              value={aggregates.total_with_shortages.toLocaleString()}
              color={T.warning}
              accent="#000000"
              icon={<IconFlag />}
            />
          </div>


          {/* View Mode Toolbar */}
          <div style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "12px 20px",
            background: T.white,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            boxShadow: T.shadow,
          }}>
            <div style={{ display: "flex", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, overflow: "hidden" }}>
              <button
                onClick={() => handleViewToggle('table')}
                title="Table view"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "6px 10px", border: "none", cursor: "pointer",
                  background: viewMode === 'table' ? T.navy : T.white,
                  color: viewMode === 'table' ? T.white : T.textLight,
                  transition: "all 0.15s",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              </button>
              <button
                onClick={() => handleViewToggle('card')}
                title="Card view"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "6px 10px", border: "none", borderLeft: `1px solid ${T.border}`,
                  cursor: "pointer",
                  background: viewMode === 'card' ? T.navy : T.white,
                  color: viewMode === 'card' ? T.white : T.textLight,
                  transition: "all 0.15s",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </button>
            </div>
          </div>


          {/* Data Table / Card List (toggle) */}
          {viewMode === 'card' ? (
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))",
              gap: 16,
            }}>
              {loading && (
                <div style={{ display: "flex", justifyContent: "center", padding: 40, gridColumn: "1 / -1" }}><IconSpinner /></div>
              )}
              {!loading && manufacturers.map(m => renderManufacturerCard(m))}
              {!loading && manufacturers.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: T.textLight, background: T.white, borderRadius: T.radius, border: `1px solid ${T.border}`, gridColumn: "1 / -1" }}>
                  {hasActiveFilters || searchQuery
                    ? "No manufacturers match the current filters."
                    : "No manufacturers found."}
                </div>
              )}
            </div>
          ) : (
            <div style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              boxShadow: T.shadow,
              overflow: "hidden",
            }}>
              {loading && (
                <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IconSpinner /></div>
              )}
              {!loading && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: T.offWhite }}>
                        <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort('name')}>
                          Manufacturer Name <SortIcon field="name" />
                        </th>
                        <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort('country')}>
                          Country <SortIcon field="country" />
                        </th>
                        <th style={{ ...thStyle, cursor: "pointer", textAlign: "center" }} onClick={() => handleSort('product_count')}>
                          Products <SortIcon field="product_count" />
                        </th>
                        <th style={{ ...thStyle, textAlign: "center" }}>CCP</th>
                        <th style={{ ...thStyle, cursor: "pointer", textAlign: "center" }} onClick={() => handleSort('shortage_count')}>
                          Shortages <SortIcon field="shortage_count" />
                        </th>
                        <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                        <th style={{ ...thStyle, cursor: "pointer", textAlign: "center" }} onClick={() => handleSort('recall_count')}>
                          Active Recalls <SortIcon field="recall_count" />
                        </th>
                        <th style={{ ...thStyle, width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {manufacturers.map((m, idx) => {
                        const hasActiveRecalls = m.active_recall_count > 0;
                        const hasShortage = m.shortage_count > 0;
                        const hasCcpFlag = !!m.ccp_flag;

                        return (
                          <tr
                            key={m.id}
                            onClick={() => onViewManufacturer(m.id)}
                            style={{
                              cursor: "pointer",
                              borderBottom: idx < manufacturers.length - 1 ? `1px solid ${T.borderLight}` : "none",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
                            onMouseLeave={e => e.currentTarget.style.background = T.white}
                          >
                            <td style={{ padding: "14px 16px", maxWidth: 320 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: T.textDark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {m.name}
                              </div>
                              {m.dea_number && (
                                <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>DEA: {m.dea_number}</div>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <span style={{ fontSize: 13, color: T.textMid }}>{m.country || "Unknown"}</span>
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{m.product_count.toLocaleString()}</span>
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              {hasCcpFlag ? (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                  textTransform: "uppercase", letterSpacing: "0.04em",
                                  background: T.dangerBg, color: T.danger, border: `1px solid ${T.danger}40`,
                                }}>
                                  CCP Alert
                                </span>
                              ) : (
                                <span style={{ fontSize: 13, color: T.textLight }}>&mdash;</span>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              {hasShortage ? (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                  background: T.warningBg, color: T.warning, border: `1px solid ${T.warning}40`,
                                }}>
                                  {m.shortage_count}
                                </span>
                              ) : (
                                <span style={{ fontSize: 13, color: T.textLight }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              {hasActiveRecalls ? (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                  textTransform: "uppercase", letterSpacing: "0.04em",
                                  background: T.dangerBg, color: T.danger, border: `1px solid ${T.danger}40`,
                                }}>
                                  Recalls
                                </span>
                              ) : hasShortage ? (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                  textTransform: "uppercase", letterSpacing: "0.04em",
                                  background: T.warningBg, color: T.warning, border: `1px solid ${T.warning}40`,
                                }}>
                                  Shortage
                                </span>
                              ) : (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                  textTransform: "uppercase", letterSpacing: "0.04em",
                                  background: T.successBg, color: T.success, border: "1px solid #BBF7D0",
                                }}>
                                  Clean
                                </span>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              {hasActiveRecalls ? (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                  background: T.dangerBg, color: T.danger, border: `1px solid ${T.danger}40`,
                                }}>
                                  {m.active_recall_count}
                                </span>
                              ) : (
                                <span style={{ fontSize: 13, color: T.textLight }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px", color: T.textLight }}>
                              <IconChevron />
                            </td>
                          </tr>
                        );
                      })}
                      {manufacturers.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ padding: 40, textAlign: "center", color: T.textLight }}>
                            {hasActiveFilters || searchQuery
                              ? "No manufacturers match the current filters."
                              : "No manufacturers found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && renderPagination()}
        </>
      )}
    </div>
  );
}
